import { useMemo } from 'react'

import { TokenResult } from '@ambire-common/libs/portfolio/interfaces'
import { Network } from '@ambire-common/interfaces/network'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useRailgunContext from '@web/hooks/useRailgunContext'
import {
  type RailgunTokenBalanceMap,
  getTokenDecimals,
  getTokenName,
  getTokenSymbol,
  priceKey
} from '@web/utils/railgunTokens'

function balanceMapToTokenList(
  balanceTokenMap: RailgunTokenBalanceMap,
  priceMap: Map<string, number>,
  networks: Network[],
  pendingPOIMaps: (RailgunTokenBalanceMap | undefined)[]
): TokenResult[] {
  const list: TokenResult[] = []
  const seen = new Set<string>()

  const addRow = (chainId: number, tokenAddress: string, amount: bigint) => {
    const key = priceKey(chainId, tokenAddress)
    if (seen.has(key)) return
    seen.add(key)
    const network = networks.find((n) => n.chainId === BigInt(chainId))
    const decimals = getTokenDecimals(chainId, tokenAddress)
    const priceUSD = priceMap.get(key) ?? 0
    const symbol = getTokenSymbol(chainId, tokenAddress, network)
    const name = getTokenName(chainId, tokenAddress, network)
    list.push({
      symbol,
      name,
      decimals,
      address: tokenAddress,
      chainId: BigInt(chainId),
      amount,
      priceIn: priceUSD > 0 ? [{ baseCurrency: 'usd', price: priceUSD }] : [],
      flags: {
        onGasTank: false,
        rewardsType: null,
        canTopUpGasTank: false,
        isFeeToken: false
      }
    } as any)
  }

  if (balanceTokenMap?.size) {
    for (const [chainId, balances] of balanceTokenMap) {
      for (const { tokenAddress, amount } of balances) {
        if (amount === 0n) continue
        addRow(chainId, tokenAddress, amount)
      }
    }
  }

  // Phase 4.4 — also surface tokens that have only pending-POI balance (no spendable yet),
  // so the dashboard shows a row with $0.00 spendable and a "+$X pending POI" subscript.
  // Without this, users see the per-chain pending POI but can't tell which token is incoming.
  for (const map of pendingPOIMaps) {
    if (!map?.size) continue
    for (const [chainId, balances] of map) {
      for (const { tokenAddress, amount } of balances) {
        if (amount === 0n) continue
        addRow(chainId, tokenAddress, 0n)
      }
    }
  }
  return list
}

/**
 * Converts current Railgun wallet's balanceTokenMap + balanceTokenPriceMap into TokenResult[]
 * for rendering in the dashboard Tokens list when in private mode.
 */
export default function useRailgunTokens(): TokenResult[] {
  const { networks } = useNetworksControllerState()
  const { currentRailgunWallet } = useRailgunContext()

  return useMemo(() => {
    const map = currentRailgunWallet?.balanceTokenMap
    const prices = currentRailgunWallet?.balanceTokenPriceMap
    const missingInternal = currentRailgunWallet?.missingInternalPOIBalanceTokenMap
    const missingExternal = currentRailgunWallet?.missingExternalPOIBalanceTokenMap
    if (!prices) return []
    const hasAny =
      Boolean(map?.size) || Boolean(missingInternal?.size) || Boolean(missingExternal?.size)
    if (!hasAny) return []
    return balanceMapToTokenList(map ?? new Map(), prices, networks, [
      missingInternal,
      missingExternal
    ])
  }, [
    currentRailgunWallet?.balanceTokenMap,
    currentRailgunWallet?.balanceTokenPriceMap,
    currentRailgunWallet?.missingInternalPOIBalanceTokenMap,
    currentRailgunWallet?.missingExternalPOIBalanceTokenMap,
    networks
  ])
}
